// The UI-19, step 18 (db/migrations/9820). Prepare reads SimplePay and shows
// the figures the form will carry; Send draws the form and puts it in front of
// the employer signatory on SignNow. Once signed it is filed here and emailed
// to the driver by Postgres. Nothing is computed or typed here: every figure is
// what SimplePay holds, read by driver_ui19_prepare_v1.
import { useCallback, useEffect } from 'react'
import { FileSignature, FileText, RefreshCw, Send } from 'lucide-react'
import { useGetOffboardingUi19, useOpenUi19, usePrepareUi19, useSendUi19 } from '../../hooks/backend/offboarding'
import { Button } from '../../lib/shadcn/button'
import { Spinner } from '../../lib/shadcn/spinner'
import { toast } from '../../lib/shadcn/sonner'
import { Chip } from '../interviews/Chip'
import { writeErrorMessage } from '../../utils/writeError'
import { formatDay, formatWhen, formatZar, type CaseDetail } from '../../utils/offboarding'

type Figures = {
  month: string
  payslip_id: string
  employee: {
    surname: string; initials: string; id_number: string; gross_per_month: number; gross_basis: string; hours: number
    date_of_employment: string; termination_date: string; termination_code: number; termination_reason: string | null
  }
  employer: { uif_reference: string | null; paye_reference: string | null; authorised_person: string | null }
}
type Ui19 = {
  id: string; status: 'drawn' | 'signing' | 'signed' | 'declined'; figures: Figures
  drawn_by: string | null; drawn_at: string; sent_by: string | null; sent_at: string | null
  signer: string | null; signer_email: string | null; signed_at: string | null; has_signed_pdf: boolean; emailed: boolean
}
type Read = { ok: boolean; may_send: boolean; signer: string | null; ui19: Ui19 | null }

const TONE = { drawn: 'neutral', signing: 'amber', signed: 'green', declined: 'red' } as const
const LABEL = { drawn: 'Prepared, not sent', signing: 'With the signatory', signed: 'Signed', declined: 'Declined on SignNow' } as const

export function Ui19Panel({ detail, onChanged }: { detail: CaseDetail; onChanged: () => void }) {
  const caseId = detail.summary.id
  const editable = ['open', 'awaiting_signoff'].includes(detail.summary.status)
  const q = useGetOffboardingUi19<Read>()
  const prepare = usePrepareUi19<{ ok: boolean }>()
  const send = useSendUi19<{ ok: boolean; signer: string }>()
  const open = useOpenUi19<{ url: string }>()
  const load = q.trigger
  const refresh = useCallback(() => { void load({ case_id: caseId }, { skipCache: true }).catch(() => undefined) }, [load, caseId])
  useEffect(() => { refresh() }, [refresh])

  const r = q.data
  if (!r) return null
  const u = r.ui19
  const f = u?.figures

  async function run(what: string, fn: () => Promise<unknown>) {
    try { await fn(); toast.success(what); refresh(); onChanged() } catch (err) { toast.error(writeErrorMessage(err)) }
  }
  async function openPdf(id: string) {
    try {
      const res = await open.trigger({ id }).result
      if (res?.url) window.open(res.url, '_blank', 'noopener')
    } catch (err) { toast.error(writeErrorMessage(err)) }
  }

  const canPrepare = editable && r.may_send && (!u || u.status === 'drawn' || u.status === 'declined')

  return (
    <section className="space-y-3 rounded-lg border border-border p-3">
      <div className="flex flex-wrap items-center gap-2">
        <FileSignature className="h-4 w-4 text-muted-foreground" />
        <h3 className="text-sm font-semibold text-foreground">UI-19</h3>
        {u && <Chip tone={TONE[u.status]}>{LABEL[u.status]}</Chip>}
      </div>

      {!u && (
        <p className="text-xs text-muted-foreground">
          Drawn from SimplePay once the employee's service is ended there and every payslip of the service period is finalised,
          then signed by {r.signer ?? 'the employer signatory'} on SignNow and emailed to the driver.
        </p>
      )}

      {f && (
        <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs sm:grid-cols-4">
          <dt className="text-muted-foreground">Month</dt><dd>{f.month}</dd>
          <dt className="text-muted-foreground">Gross per month</dt><dd>{formatZar(f.employee.gross_per_month)}</dd>
          <dt className="text-muted-foreground">Hours worked</dt><dd>{f.employee.hours}</dd>
          <dt className="text-muted-foreground">Employee</dt><dd>{f.employee.surname} {f.employee.initials}, {f.employee.id_number}</dd>
          <dt className="text-muted-foreground">Employed</dt><dd>{formatDay(f.employee.date_of_employment)} to {formatDay(f.employee.termination_date)}</dd>
          <dt className="text-muted-foreground">Reason</dt><dd>{f.employee.termination_code} {f.employee.termination_reason ?? ''}</dd>
          <dt className="text-muted-foreground">UIF reference</dt><dd>{f.employer.uif_reference ?? '—'}</dd>
          <dt className="text-muted-foreground">PAYE reference</dt><dd>{f.employer.paye_reference ?? '—'}</dd>
        </dl>
      )}
      {f && <p className="text-xs text-muted-foreground">{f.employee.gross_basis}. Prepared by {u?.drawn_by ?? 'someone'} on {formatWhen(u!.drawn_at)}.</p>}

      {u?.status === 'signing' && (
        <p className="text-xs text-muted-foreground">
          Sent by {u.sent_by} on {formatWhen(u.sent_at)} to {u.signer} ({u.signer_email}) on SignNow. Signatures are checked every 15 minutes;
          once signed it is filed here and emailed to the driver.
        </p>
      )}
      {u?.status === 'signed' && (
        <p className="text-xs text-muted-foreground">
          Signed by {u.signer} on {formatWhen(u.signed_at)}. {u.emailed ? 'Emailed to the driver with the signed PDF attached.' : 'Not emailed: there is no email address for the driver. Give it to them and record delivery at step 20.'}
          {' '}Step 18 still needs the termination declared on uFiling.
        </p>
      )}
      {u?.status === 'declined' && (
        <p className="text-xs text-rose-700 dark:text-rose-300">
          The signatory declined it. Check the figures against SimplePay, correct SimplePay if needed, and prepare it again.
        </p>
      )}

      <div className="flex flex-wrap gap-2">
        {canPrepare && (
          <Button size="sm" variant={u ? 'outline' : 'brand'} disabled={prepare.loading}
            onClick={() => void run('UI-19 prepared from SimplePay', () => prepare.trigger({ case_id: caseId }).result)}>
            {prepare.loading ? <Spinner className="mr-1 h-3 w-3" /> : <RefreshCw className="mr-1 h-3 w-3" />}
            {u ? 'Read SimplePay again' : 'Prepare UI-19 from SimplePay'}
          </Button>
        )}
        {editable && r.may_send && u?.status === 'drawn' && (
          <Button size="sm" variant="brand" disabled={send.loading}
            onClick={() => void run(`UI-19 sent to ${r.signer ?? 'the signatory'} on SignNow`, () => send.trigger({ id: u.id }).result)}>
            {send.loading ? <Spinner className="mr-1 h-3 w-3" /> : <Send className="mr-1 h-3 w-3" />}
            Send to {r.signer ?? 'the signatory'} for signature
          </Button>
        )}
        {u && (u.status === 'signing' || u.status === 'signed') && (
          <Button size="sm" variant="outline" disabled={open.loading} onClick={() => void openPdf(u.id)}>
            <FileText className="mr-1 h-3 w-3" /> {u.has_signed_pdf ? 'Open the signed UI-19' : 'Open the UI-19 sent'}
          </Button>
        )}
      </div>
    </section>
  )
}
